import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type { Category, City, Poi } from '../api/types';
import { PlaceBrowser } from './PlaceBrowser';

/*
 * The place browser, opened over a round being built.
 *
 * A window rather than a page because it is reached mid-decision: the form
 * is behind it, half filled in, and closing this returns to it exactly as
 * it was. Everything inside is PlaceBrowser, which also stands on its own
 * page — see CoveragePage. This is the frame and nothing else, so the two can
 * never drift apart.
 */

export function PlaceChooserDialog({
  open, city, categories, basemapUrl, onClose,
  onSetStart, onSetEnd, onAddStop, inRound = [], startId, endId,
}: {
  open: boolean;
  city: City;
  /** The city's own categories, for the filter. */
  categories: Category[];
  /** The tiles the page's own map draws, for this one's map view. */
  basemapUrl?: string | null;
  onClose: () => void;
  onSetStart: (poi: Poi) => void;
  onSetEnd: (poi: Poi) => void;
  onAddStop: (poi: Poi) => void;
  /** Ids already named in the round, so the list can say so. */
  inRound?: string[];
  startId?: string | undefined;
  endId?: string | undefined;
}) {
  /*
    The whole screen, rather than a window on it.

    Two panes that each want room — a catalogue to scroll and a map to read
    ground off — and at window size neither had it: the map was a few
    hundred pixels of a city, which is the size at which a map stops
    answering questions. Nothing behind it is being read while it is open.
  */
  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h2">Choose places</Typography>
            <Typography variant="caption" color="text.secondary">
              {`Every visitable place in ${city.name}. Pick one to see what has been planned there before.`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <PlaceBrowser
          city={city}
          categories={categories}
          active={open}
          {...(basemapUrl ? { basemapUrl } : {})}
          onSetStart={onSetStart}
          onSetEnd={onSetEnd}
          onAddStop={onAddStop}
          inRound={inRound}
          {...(startId ? { startId } : {})}
          {...(endId ? { endId } : {})}
        />
      </DialogContent>
    </Dialog>
  );
}
